import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import '../../styles/carousel-fonts.css';

let domPurifyCarouselHooksInstalled = false;
const installCarouselDomPurifyHooks = () => {
  if (domPurifyCarouselHooksInstalled) return;
  domPurifyCarouselHooksInstalled = true;

  // Разрешаем <img> только с src из нашего S3 и чистим style на <img>
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const tagName = (node as any)?.tagName?.toUpperCase?.() || '';
    const attrName = (data as any)?.attrName || '';
    const attrValue = String((data as any)?.attrValue ?? '');

    if (tagName === 'IMG' && attrName === 'src') {
      if (!attrValue.startsWith('https://s3.twcstorage.ru/')) {
        (data as any).keepAttr = false;
      }
      return;
    }

    if (tagName === 'IMG' && attrName === 'style') {
      const allowedProps = new Set([
        'position',
        'top',
        'left',
        'right',
        'bottom',
        'z-index',
        'width',
        'height',
        'object-fit',
        'object-position',
        'border-radius',
        'border-top-left-radius',
        'border-top-right-radius',
        'border-bottom-left-radius',
        'border-bottom-right-radius',
        'transform',
        'opacity',
        'filter',
        'display',
        'flex',
        'flex-direction',
        'justify-content',
        'align-items',
        'margin',
        'margin-top',
        'margin-bottom',
        'margin-left',
        'margin-right',
        'padding',
        'padding-top',
        'padding-bottom',
        'padding-left',
        'padding-right',
        'border',
        'border-left',
        'border-right',
        'border-top',
        'border-bottom',
        'box-sizing',
        'background',
        'background-color',
        'background-image',
        'background-size',
        'background-position',
        'background-repeat',
        'font-family',
        'font-size',
        'font-weight',
        'line-height',
        'letter-spacing',
        'text-transform',
        'text-align',
        'overflow',
        'gap',
        'box-shadow',
        'color',
      ]);

      const safe = attrValue
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)
        .map((decl) => {
          const idx = decl.indexOf(':');
          if (idx <= 0) return null;
          const key = decl.slice(0, idx).trim().toLowerCase();
          const val = decl.slice(idx + 1).trim();
          if (!allowedProps.has(key)) return null;
          if (!val) return null;
          return `${key}: ${val}`;
        })
        .filter(Boolean)
        .join('; ');

      (data as any).attrValue = safe;
    }
  });
};

installCarouselDomPurifyHooks();

// Максимально широкая (но безопасная) allowlist для HTML карточек.
// Важно: мы НЕ разрешаем внешние ресурсы/ссылки/скрипты — только структурные и типографические теги + inline styles.
const CAROUSEL_ALLOWED_TAGS: string[] = [
  // layout/structure
  'div', 'span', 'p',
  'header', 'footer', 'main', 'section', 'article',
  // headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // inline typography
  'strong', 'em', 'b', 'i', 'u', 's', 'small', 'mark',
  'sup', 'sub',
  // lists
  'ul', 'ol', 'li',
  // code/quotes
  'code', 'pre', 'blockquote',
  // misc
  'br', 'hr',
  // optional wrappers (без атрибутов, кроме style)
  'a',
  // tables (иногда удобны для сеток, но без опасных атрибутов)
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  // images (строго ограничены hook'ом)
  'img',
  // SVG элементы
  'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse',
  'g', 'defs', 'use', 'text', 'tspan', 'textPath', 'clipPath', 'mask',
  'linearGradient', 'radialGradient', 'stop', 'pattern', 'image',
];

const CAROUSEL_DOMPURIFY_CONFIG: import('dompurify').Config = {
  ALLOWED_TAGS: CAROUSEL_ALLOWED_TAGS,
  // Разрешаем ТОЛЬКО inline styles + минимальные атрибуты для img + SVG атрибуты
  ALLOWED_ATTR: [
    'style', 'src', 'alt',
    // SVG атрибуты
    'viewBox', 'xmlns', 'xmlns:xlink', 'width', 'height', 'x', 'y',
    'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
    'd', 'cx', 'cy', 'r', 'rx', 'ry', 'points', 'x1', 'y1', 'x2', 'y2',
    'transform', 'opacity', 'fill-opacity', 'stroke-opacity',
    'clip-path', 'mask', 'id', 'class',
  ],
  // Дублируем запреты на случай, если DOMPurify начнёт "умничать" с профилями.
  FORBID_TAGS: [
    'script', 'style', 'link',
    'canvas',
    'video', 'audio', 'source',
    'iframe', 'object', 'embed',
    'form', 'input', 'textarea', 'select', 'option', 'button',
    'meta', 'base',
  ],
  FORBID_ATTR: [
    // события
    'on*',
    // внешние загрузки/навигация
    'href', 'srcset',
    // потенциальные вектора (xlink:href разрешен для use элементов)
  ],
  RETURN_TRUSTED_TYPE: false,
};

type CarouselCardsPreviewProps = {
  carouselHtmlJson: string;
  /**
   * Если передан, "Редактировать" появится во fullscreen и вызовет onEditCard(index)
   */
  onEditCard?: (index: number) => void;
  /**
   * Если передан, "Скачать" появится во fullscreen и вызовет onDownloadCard(index)
   */
  onDownloadCard?: (index: number) => void;
  /**
   * Контролируемый индекс (опционально). Если не передан — компонент ведёт свой.
   */
  index?: number;
  onIndexChange?: (nextIndex: number) => void;
};

export const CarouselCardsPreview: React.FC<CarouselCardsPreviewProps> = ({
  carouselHtmlJson,
  onEditCard,
  onDownloadCard,
  index: controlledIndex,
  onIndexChange,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [internalIndex, setInternalIndex] = useState(0);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [fontsReady, setFontsReady] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const touchStartRef = useRef<{ x: number; y: number; active: boolean }>({ x: 0, y: 0, active: false });

  const index = controlledIndex ?? internalIndex;
  const setIndex = (next: number) => {
    setInternalIndex(next);
    onIndexChange?.(next);
  };

  useEffect(() => {
    if (controlledIndex === undefined) return;
    setInternalIndex(controlledIndex);
  }, [controlledIndex]);

  useEffect(() => {
    if (wrapperRef.current) {
      setAvailableWidth(wrapperRef.current.offsetWidth);
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          setAvailableWidth(entry.contentRect.width);
        }
      }
    });
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const cards = useMemo(() => {
    try {
      const parsed = JSON.parse(carouselHtmlJson);
      return Array.isArray(parsed) ? (parsed as unknown[]).filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }, [carouselHtmlJson]);

  const sanitizedCards = useMemo(() => {
    return cards.map((html) => {
      const sanitized = DOMPurify.sanitize(html, CAROUSEL_DOMPURIFY_CONFIG) as unknown as string;
      return sanitized;
    });
  }, [cards]);

  const extractPrimaryFonts = (html: string): string[] => {
    // Берём только первое семейство из font-family, чтобы дождаться именно его (иначе будет fallback-метрика).
    const fonts = new Set<string>();
    const matches = html.match(/font-family\s*:\s*([^;"]+)/gi) ?? [];
    for (const m of matches) {
      const value = m.split(':')[1]?.trim() ?? '';
      const first = value.split(',')[0]?.trim() ?? '';
      const cleaned = first.replace(/^['"]|['"]$/g, '').trim();
      if (cleaned) fonts.add(cleaned);
    }
    return Array.from(fonts);
  };

  // Функция для определения формата карточки (квадратная 1080x1080 или вертикальная 1080x1350)
  const detectCardFormat = (html: string): { width: number; height: number; isSquare: boolean } => {
    // Пытаемся найти размеры в стилях
    const widthRegex = /width\s*:\s*(\d+)px/gi;
    const heightRegex = /height\s*:\s*(\d+)px/gi;
    
    let width = 1080;
    let height = 1350;
    
    // Ищем ширину
    let match = widthRegex.exec(html);
    while (match) {
      const w = parseInt(match[1], 10);
      if (w === 1080) {
        width = w;
        break;
      }
      match = widthRegex.exec(html);
    }
    
    // Ищем высоту
    match = heightRegex.exec(html);
    while (match) {
      const h = parseInt(match[1], 10);
      if (h === 1080 || h === 1350) {
        height = h;
        break;
      }
      match = heightRegex.exec(html);
    }
    
    // Если нашли оба размера, определяем формат
    if (width === 1080 && (height === 1080 || height === 1350)) {
      const isSquare = height === 1080;
      return { width, height, isSquare };
    }
    
    // Если не нашли в стилях, создаем временный элемент для измерения
    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.width = '1080px';
      tempDiv.style.height = 'auto';
      tempDiv.style.top = '-9999px';
      tempDiv.innerHTML = html;
      document.body.appendChild(tempDiv);
      
      // Ждем немного для рендеринга
      const naturalHeight = tempDiv.scrollHeight;
      document.body.removeChild(tempDiv);
      
      // Определяем формат по естественной высоте
      const isSquare = Math.abs(naturalHeight - 1080) < 50;
      return { width: 1080, height: isSquare ? 1080 : 1350, isSquare };
    } catch {
      // По умолчанию считаем вертикальной
      return { width: 1080, height: 1350, isSquare: false };
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const fontSet = (document as any).fonts as FontFaceSet | undefined;
        if (!fontSet || typeof fontSet.load !== 'function') {
          if (!cancelled) setFontsReady(true);
          return;
        }

        const families = sanitizedCards.flatMap(extractPrimaryFonts);
        if (families.length === 0) {
          if (!cancelled) setFontsReady(true);
          return;
        }

        await Promise.all(
          families.map((f) => fontSet.load(`400 16px "${f}"`))
        );
        await fontSet.ready;
      } catch {
        // ignore
      } finally {
        if (!cancelled) setFontsReady(true);
      }
    };

    setFontsReady(false);
    void run();
    return () => {
      cancelled = true;
    };
  }, [sanitizedCards]);

  const clampIndex = (nextIndex: number) => Math.max(0, Math.min(nextIndex, sanitizedCards.length - 1));

  const handleScrollTo = (nextIndex: number) => {
    const clamped = clampIndex(nextIndex);
    setIndex(clamped);
  };

  const openLightbox = (i: number) => {
    const clamped = clampIndex(i);
    setLightboxIndex(clamped);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const gotoLightbox = (next: number) => setLightboxIndex(clampIndex(next));

  // Swipe
  const touchStartXRef = useRef<number>(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? 0;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const end = e.changedTouches[0]?.clientX ?? touchStartXRef.current;
    const delta = touchStartXRef.current - end;
    if (Math.abs(delta) > 50) {
      if (delta > 0) gotoLightbox(lightboxIndex + 1);
      else gotoLightbox(lightboxIndex - 1);
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') gotoLightbox(lightboxIndex - 1);
      if (e.key === 'ArrowRight') gotoLightbox(lightboxIndex + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxOpen, lightboxIndex]);

  if (sanitizedCards.length === 0) {
    return (
      <div className="text-xs text-gray-600 dark:text-gray-300">
        Не удалось распарсить карточки. Ожидался JSON-массив HTML-строк.
      </div>
    );
  }

  // Определяем формат карточки (предполагаем, что все карточки в карусели имеют одинаковый формат)
  const cardFormat = useMemo(() => {
    if (sanitizedCards.length > 0) {
      return detectCardFormat(sanitizedCards[0]);
    }
    return { width: 1080, height: 1350, isSquare: false };
  }, [sanitizedCards]);

  // Рисуем "как дал ИИ": холст 1080x1350 или 1080x1080, масштабируем transform'ом.
  // В чате/превью делаем карточки компактнее, чтобы они не "съедали" весь экран.
  const maxCardWidth = (availableWidth || 0) >= 360 ? 260 : 220;
  const cardWidth = Math.max(180, Math.min(availableWidth || 1, maxCardWidth));
  const scale = cardWidth / cardFormat.width;
  const cardHeight = cardWidth * (cardFormat.height / cardFormat.width);
  const gapPx = 12; // соответствует gap-3

  const activeLightboxHtml = sanitizedCards[lightboxIndex] ?? '';
  const centeringOffset = (availableWidth - cardWidth) / 2;

  const handlePreviewTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, active: true };
    setDragOffsetX(0);
  };

  const handlePreviewTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current.active) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;

    // ВАЖНО для мобильного чата: оставляем вертикальный скролл родителя доступным.
    // Поэтому мы реагируем на горизонталь (dx) и не пытаемся "глушить" dy.
    if (Math.abs(dx) > Math.abs(dy) + 6) {
      setDragOffsetX(dx);
    } else {
      setDragOffsetX(0);
    }
  };

  const handlePreviewTouchEnd = () => {
    if (!touchStartRef.current.active) return;
    touchStartRef.current.active = false;

    const dx = dragOffsetX;
    const threshold = Math.max(40, cardWidth * 0.18);
    if (dx <= -threshold) handleScrollTo(index + 1);
    else if (dx >= threshold) handleScrollTo(index - 1);
    setDragOffsetX(0);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="mb-2">
        <div className="text-[10px] md:text-xs text-gray-600 dark:text-gray-300">
          Карточка {index + 1} из {sanitizedCards.length}
        </div>
      </div>

      <div
        className="relative overflow-hidden"
        style={{ height: cardHeight, touchAction: 'pan-y' }}
        onTouchStart={handlePreviewTouchStart}
        onTouchMove={handlePreviewTouchMove}
        onTouchEnd={handlePreviewTouchEnd}
        onTouchCancel={handlePreviewTouchEnd}
      >
        <div
          className="flex gap-3 will-change-transform"
          style={{
            transform: `translate3d(${centeringOffset - (index * (cardWidth + gapPx)) + dragOffsetX}px, 0, 0)`,
            transition: touchStartRef.current.active ? 'none' : 'transform 300ms cubic-bezier(0.2, 0, 0.2, 1)',
          }}
        >
          {sanitizedCards.map((html, i) => (
            <button
              key={i}
              type="button"
              onClick={() => openLightbox(i)}
              className={`shrink-0 text-left overflow-hidden transition-opacity duration-300 ${
                i === index ? 'opacity-100 scale-100' : 'opacity-40 scale-[0.92]'
              }`}
              style={{ 
                width: cardWidth, 
                height: cardHeight,
                transformOrigin: 'center center'
              }}
              aria-label={`Открыть карточку ${i + 1} на весь экран`}
            >
              <div
                style={{
                  width: cardFormat.width,
                  height: cardFormat.height,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  visibility: fontsReady ? 'visible' : 'hidden',
                  fontSize: '16px',
                  lineHeight: 'normal',
                  color: '#000000',
                  textAlign: 'left',
                }}
                dangerouslySetInnerHTML={{ __html: html }}
              />
              {!fontsReady && (
                <div className="text-[10px] text-gray-500 dark:text-gray-400">
                  Загружаем шрифты…
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Кнопки переключения снизу */}
      <div className="flex items-center justify-center mt-3 gap-3">
        <button
          type="button"
          onClick={() => handleScrollTo(index - 1)}
          disabled={index === 0}
          className="flex-1 max-w-[80px] py-1.5 flex items-center justify-center rounded-xl bg-black/5 dark:bg-white/10 backdrop-blur-md text-gray-700 dark:text-gray-200 disabled:opacity-20 active:scale-95 transition-all"
          aria-label="Предыдущая карточка"
        >
          <span className="text-base font-bold">←</span>
        </button>

        <button
          type="button"
          onClick={() => handleScrollTo(index + 1)}
          disabled={index === sanitizedCards.length - 1}
          className="flex-1 max-w-[80px] py-1.5 flex items-center justify-center rounded-xl bg-black/5 dark:bg-white/10 backdrop-blur-md text-gray-700 dark:text-gray-200 disabled:opacity-20 active:scale-95 transition-all"
          aria-label="Следующая карточка"
        >
          <span className="text-base font-bold">→</span>
        </button>
      </div>

      {lightboxOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[15000]">
          <div
            className="absolute inset-0 bg-black/85"
            onClick={closeLightbox}
            aria-label="Закрыть просмотр"
          />

          <div
            className="absolute inset-0 flex flex-col"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="flex items-center justify-between px-3 py-2">
              <div className="text-xs text-white/80">
                {lightboxIndex + 1}/{sanitizedCards.length}
              </div>
            </div>

            <div className="relative flex-1 overflow-hidden">
              <button
                type="button"
                onClick={() => gotoLightbox(lightboxIndex - 1)}
                disabled={lightboxIndex === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-xl bg-white/12 hover:bg-white/18 disabled:opacity-30 text-white border border-white/20 backdrop-blur flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                aria-label="Предыдущая карточка"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => gotoLightbox(lightboxIndex + 1)}
                disabled={lightboxIndex === sanitizedCards.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-xl bg-white/12 hover:bg-white/18 disabled:opacity-30 text-white border border-white/20 backdrop-blur flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                aria-label="Следующая карточка"
              >
                →
              </button>

              <div 
                className="absolute inset-0 flex items-center justify-center p-3 touch-none overflow-hidden"
                style={{ overscrollBehavior: 'none' }}
              >
                <div className="w-full flex justify-center py-12">
                  {(() => {
                    const fitScale = Math.min(
                      (window.innerWidth - 24) / cardFormat.width,
                      (window.innerHeight - 240) / cardFormat.height
                    );
                    const fitW = Math.max(1, Math.floor(cardFormat.width * fitScale));
                    const fitH = Math.max(1, Math.floor(cardFormat.height * fitScale));
                    return (
                      <div className="w-fit">
                        <div
                          className="relative bg-black"
                          style={{ width: fitW, height: fitH }}
                        >
                          <div
                            className="absolute left-0 top-0"
                            style={{
                              width: cardFormat.width,
                              height: cardFormat.height,
                              transform: `scale(${fitScale})`,
                              transformOrigin: 'top left',
                              visibility: fontsReady ? 'visible' : 'hidden',
                              fontSize: '16px',
                              lineHeight: 'normal',
                              color: '#000000',
                              textAlign: 'left',
                            }}
                            dangerouslySetInnerHTML={{ __html: activeLightboxHtml }}
                          />
                        </div>
                        {!fontsReady && (
                          <div className="mt-2 text-xs text-white/60 text-center">
                            Загружаем шрифты…
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Кнопки внизу */}
            <div className="px-4 py-4 pb-8 flex items-center justify-center gap-3">
              {onEditCard && (
                <button
                  type="button"
                  onClick={() => onEditCard(lightboxIndex)}
                  className="flex-1 max-w-[160px] px-4 py-2.5 text-sm font-medium rounded-xl text-white border border-white/25 bg-white/10 hover:bg-white/15 backdrop-blur transition-colors"
                  aria-label="Редактировать текущую карточку"
                >
                  Редактировать
                </button>
              )}
              {onDownloadCard && (
                <button
                  type="button"
                  onClick={() => onDownloadCard(lightboxIndex)}
                  className="flex-1 max-w-[160px] px-4 py-2.5 text-sm font-medium rounded-xl text-white border border-white/25 bg-white/10 hover:bg-white/15 backdrop-blur transition-colors"
                  aria-label="Скачать текущую карточку"
                >
                  Скачать
                </button>
              )}
              <button
                type="button"
                onClick={closeLightbox}
                className="flex-1 max-w-[160px] px-4 py-2.5 text-sm font-medium rounded-xl text-white border border-white/25 bg-white/10 hover:bg-white/15 backdrop-blur transition-colors"
                aria-label="Закрыть"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


